import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Button, ErrorNote, Field, Input, Select, StatusBadge } from './ui';
import {
  DEAL_CONTACT_ROLES,
  api,
  type Deal,
  type DealContact,
  type DealContactRole,
  type Prospect,
  type ProspectDealRole,
} from '../lib/api';
import { moneyIn } from '../lib/format';
import { useAction, useApi, useDebounced } from '../lib/hooks';

const roleLabel = (role: string) => DEAL_CONTACT_ROLES.find((r) => r.key === role)?.label ?? role;

function RoleSelect({
  value,
  onChange,
  disabled,
  label,
}: {
  value: DealContactRole;
  onChange: (role: DealContactRole) => void;
  disabled?: boolean;
  label: string;
}) {
  return (
    <Select
      value={value}
      disabled={disabled}
      aria-label={label}
      onChange={(event) => onChange(event.target.value as DealContactRole)}
      className="w-auto !py-1 text-xs"
    >
      {DEAL_CONTACT_ROLES.map((role) => (
        <option key={role.key} value={role.key}>
          {role.label}
        </option>
      ))}
    </Select>
  );
}

/**
 * The buying committee on a deal: the primary contact (the deal's own prospect,
 * unchanged by anything here) followed by everyone given a role. Adding searches
 * prospects the same way the New deal form does.
 */
export function DealContactsPanel({
  deal,
  contacts,
  onChanged,
}: {
  deal: Deal;
  contacts: DealContact[];
  onChanged: () => void;
}) {
  const [search, setSearch] = useState('');
  const [role, setRole] = useState<DealContactRole>('champion');
  const debounced = useDebounced(search);
  const results = useApi<{ prospects: Prospect[] }>(
    debounced ? `/api/prospects?search=${encodeURIComponent(debounced)}&limit=8` : null,
  );

  const add = useAction(async (prospectId: string) => {
    await api.post(`/api/deals/${deal.id}/contacts`, { prospect_id: prospectId, role });
    setSearch('');
    onChanged();
  });
  const change = useAction(async (contactId: string, next: DealContactRole) => {
    await api.put(`/api/deals/contacts/${contactId}`, { role: next });
    onChanged();
  });
  const remove = useAction(async (contactId: string) => {
    await api.del(`/api/deals/contacts/${contactId}`);
    onChanged();
  });

  const error = add.error ?? change.error ?? remove.error;

  return (
    <div>
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-500">People on this deal</h3>
      <ul className="grid gap-1.5">
        <li className="flex flex-wrap items-center gap-2 text-sm">
          <Link to={`/prospects/${deal.prospect_id}`} className="text-accent-700 underline underline-offset-2">
            {deal.prospect_name || 'Primary contact'}
          </Link>
          <Badge tone="blue">Primary contact</Badge>
        </li>
        {contacts.map((contact) => (
          <li key={contact.id} className="flex flex-wrap items-center gap-2 text-sm">
            <Link to={`/prospects/${contact.prospect_id}`} className="text-accent-700 underline underline-offset-2">
              {contact.prospect_name || 'Unnamed contact'}
            </Link>
            {contact.job_title && <span className="text-xs text-ink-500">{contact.job_title}</span>}
            <RoleSelect
              value={contact.role}
              disabled={change.pending}
              label={`Role of ${contact.prospect_name}`}
              onChange={(next) => void change.run(contact.id, next)}
            />
            <Button size="sm" variant="ghost" loading={remove.pending} onClick={() => void remove.run(contact.id)}>
              Remove
            </Button>
          </li>
        ))}
      </ul>

      <div className="mt-3 grid gap-2 rounded-md bg-ink-50 p-3">
        <div className="grid gap-2 sm:grid-cols-[1fr_auto]">
          <Field label="Add a person" hint="Search by name, company or email.">
            <Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Name or company" />
          </Field>
          <Field label="Role">
            <RoleSelect value={role} onChange={setRole} label="Role for the person you add" />
          </Field>
        </div>
        {debounced && (
          <div className="max-h-40 overflow-y-auto rounded-md bg-white ring-1 ring-ink-200">
            {results.loading ? (
              <p className="px-3 py-2 text-sm text-ink-500">Searching…</p>
            ) : results.data?.prospects.length ? (
              results.data.prospects.map((prospect) => (
                <button
                  key={prospect.id}
                  disabled={add.pending}
                  onClick={() => void add.run(prospect.id)}
                  className="block w-full px-3 py-2 text-left text-sm hover:bg-ink-50"
                >
                  {prospect.first_name} {prospect.last_name}
                  <span className="text-ink-500"> · {prospect.company_name}</span>
                  <span className="text-xs text-accent-700"> · add as {roleLabel(role).toLowerCase()}</span>
                </button>
              ))
            ) : (
              <p className="px-3 py-2 text-sm text-ink-500">No match</p>
            )}
          </div>
        )}
      </div>
      {error && (
        <div className="mt-2">
          <ErrorNote message={error} />
        </div>
      )}
    </div>
  );
}

/**
 * The same roles from the person's side, on their record: which deals they are
 * on and as what, with the role editable and removable. Adding picks from open
 * deals; the deal's own primary contact is not listed here (it is on the deal).
 */
export function ProspectDealRolesCard({
  prospectId,
  roles,
  onChanged,
}: {
  prospectId: string;
  roles: ProspectDealRole[];
  onChanged: () => void;
}) {
  const [dealId, setDealId] = useState('');
  const [role, setRole] = useState<DealContactRole>('champion');
  const openDeals = useApi<{ deals: Deal[] }>('/api/deals?state=open&limit=200');

  const add = useAction(async () => {
    await api.post(`/api/deals/${dealId}/contacts`, { prospect_id: prospectId, role });
    setDealId('');
    onChanged();
  });
  const change = useAction(async (contactId: string, next: DealContactRole) => {
    await api.put(`/api/deals/contacts/${contactId}`, { role: next });
    onChanged();
  });
  const remove = useAction(async (contactId: string) => {
    await api.del(`/api/deals/contacts/${contactId}`);
    onChanged();
  });
  const error = add.error ?? change.error ?? remove.error;

  return (
    <div>
      {roles.length === 0 ? (
        <p className="text-sm text-ink-500">Not named on anyone else's deal.</p>
      ) : (
        <ul className="grid gap-2">
          {roles.map((entry) => (
            <li key={entry.id} className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-ink-50 px-3 py-2">
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-ink-900">{entry.deal_name || 'Untitled deal'}</p>
                <p className="text-xs text-ink-500">{moneyIn(entry.value, entry.currency)}</p>
              </div>
              <div className="flex items-center gap-2">
                <StatusBadge status={entry.stage} />
                <RoleSelect
                  value={entry.role}
                  disabled={change.pending}
                  label={`Role on ${entry.deal_name || 'deal'}`}
                  onChange={(next) => void change.run(entry.id, next)}
                />
                <Button size="sm" variant="ghost" loading={remove.pending} onClick={() => void remove.run(entry.id)}>
                  Remove
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-3 flex flex-wrap items-end gap-2">
        <Select value={dealId} onChange={(event) => setDealId(event.target.value)} aria-label="Deal to add this person to" className="w-auto">
          <option value="">Add to a deal…</option>
          {openDeals.data?.deals
            .filter((deal) => deal.prospect_id !== prospectId)
            .map((deal) => (
              <option key={deal.id} value={deal.id}>
                {deal.name || deal.company_name || 'Untitled deal'}
              </option>
            ))}
        </Select>
        <RoleSelect value={role} onChange={setRole} label="Role on that deal" />
        <Button size="sm" disabled={!dealId} loading={add.pending} onClick={() => void add.run()}>
          Add
        </Button>
      </div>
      {error && (
        <div className="mt-2">
          <ErrorNote message={error} />
        </div>
      )}
    </div>
  );
}
